import { NavLink } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import type { UserRole } from '@/types/models';
import { useState } from 'react';
import { db } from '@/lib/db';
import { syncNow } from '@/lib/sync';
import { useRow } from '@/hooks/useLive';
import { useConcreteAccess } from '@/lib/concrete';
import type { Site } from '@/types/models';
import { useUiStore } from '@/stores/uiStore';
import { APP_NAME, LOGOS } from '@/lib/brand';
import { moduleForPath } from '@/lib/access';
import { useRows } from '@/hooks/useLive';
import { usePlantAccess } from '@/lib/plant';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  roles?: UserRole[];
}

/** Navigation mirrors the module list in section 2 of the specification.
 *  `roles` hides what a user cannot use; RLS is what actually stops them. */
const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: '▦' },
  { to: '/company', label: 'Company', icon: '◫', roles: ['manager', 'admin'] },
  { to: '/diary', label: 'Daily Diary', icon: '☰' },
  { to: '/machines', label: 'Fleet', icon: '⛏' },
  { to: '/production', label: 'Production', icon: '⇪', roles: ['supervisor', 'manager', 'admin'] },
  { to: '/blasts', label: 'Blasting', icon: '✷', roles: ['supervisor', 'manager', 'admin'] },
  { to: '/breakdowns', label: 'Breakdowns', icon: '⚠' },
  { to: '/fleet/checks', label: 'Vehicle checks', icon: '✓' },
  { to: '/fuel', label: 'Fuel', icon: '⛽' },
  { to: '/workshop', label: 'Workshop', icon: '⚒', roles: ['maintenance', 'manager', 'admin'] },
  { to: '/maintenance', label: 'Maintenance', icon: '⚙', roles: ['maintenance', 'manager', 'admin'] },
  { to: '/parts', label: 'Parts & Stock', icon: '⛁', roles: ['maintenance', 'manager', 'admin'] },
  { to: '/targets', label: 'Targets', icon: '◎', roles: ['manager', 'admin'] },
  { to: '/reports', label: 'Reports', icon: '▤', roles: ['supervisor', 'manager', 'admin'] },
  { to: '/admin', label: 'Administration', icon: '⚑', roles: ['manager', 'admin'] },
];

/** The concrete module (0035). Quarry management sees it below the quarry
 *  menu; a branch user sees it beside the branch's own screens. */
const CONCRETE_NAV: (NavItem & { show?: (a: ReturnType<typeof useConcreteAccess>) => boolean })[] = [
  { to: '/concrete', label: 'Concrete dashboard', icon: '▦' },
  { to: '/concrete/deliveries', label: 'Deliveries', icon: '⛟' },
  { to: '/concrete/targets', label: 'Concrete targets', icon: '◎',
    show: (a) => !a.isBranch || a.canLead(a.branchId) },
  { to: '/concrete/lists', label: 'Mixes & customers', icon: '☷' },
  { to: '/concrete/settings', label: 'Branch settings', icon: '⚙',
    show: (a) => !a.isBranch || a.canLead(a.branchId) },
];

/** A branch user's own screens, each by its module. They do not depend on
 *  the Concrete module: a branch user without it still has fleet and fuel. */
const BRANCH_NAV: NavItem[] = [
  { to: '/diary', label: 'Daily Diary', icon: '☰' },
  { to: '/fleet/branch', label: 'Fleet', icon: '⛍' },
  { to: '/fleet/checks', label: 'Vehicle checks', icon: '✓' },
  { to: '/fleet/breakdowns', label: 'Breakdowns', icon: '⚠' },
  { to: '/fuel', label: 'Fuel', icon: '⛽' },
];

/** Concrete's own screens come first, then the branch's, then concrete's settings. */
const CONCRETE_FIRST = ['/concrete', '/concrete/deliveries'];

const HELP: NavItem = { to: '/help', label: 'Help', icon: '?' };

interface SidebarProps {
  open: boolean;
  onNavigate: () => void;
}

export function Sidebar({ open, onNavigate }: SidebarProps) {
  const profile = useAuthStore((s) => s.profile);
  const signOut = useAuthStore((s) => s.signOut);
  // Signing out wipes this device's copy, so a shared tablet never hands
  // one person's shift to the next. That includes anything not yet sent —
  // so when something is waiting, say so before it is lost.
  const [unsent, setUnsent] = useState<number | null>(null);
  const collapsed = useUiStore((s) => s.navCollapsed);
  const toggleNav = useUiStore((s) => s.toggleNav);
  const setNavCollapsed = useUiStore((s) => s.setNavCollapsed);

  async function requestSignOut() {
    const waiting = (await db.outbox.count()) + (await db.evidence_blobs.count());
    if (waiting > 0) {
      // The warning needs the room: open the rail out first.
      if (collapsed) setNavCollapsed(false);
      setUnsent(waiting);
      return;
    }
    await signOut();
  }

  const concrete = useConcreteAccess();
  const branch = useRow<Site>('sites', concrete.branchId ?? undefined);
  const sites = useRows<Site>('sites', 'name');
  const memberBranchCount = useAuthStore((s) => s.memberBranches().length);
  const selectBranch = useAuthStore((s) => s.selectBranch);
  // Subscribed so the menu redraws when access arrives from the server.
  useAuthStore((s) => s.access);
  const hasModule = useAuthStore.getState().hasModule;
  const inModule = (to: string) => {
    const m = moduleForPath(to);
    return !m || hasModule(m);
  };
  // A branch user with more than one branch works in one at a time (0055).
  const myBranches = concrete.isBranch && memberBranchCount > 1
    ? (sites ?? []).filter((x) => useAuthStore.getState().memberBranches().includes(x.id))
    : [];

  const quarry = concrete.isBranch ? [] : NAV.filter(
    // 0055: the module list decides (by default it is exactly the role's
    // menu). Administration also needs a manager's or administrator's role.
    (item) => inModule(item.to)
      && (item.to !== '/admin' || (!!profile && !!item.roles?.includes(profile.role))),
  );
  const concreteItems = concrete.canUse
    ? CONCRETE_NAV.filter((i) => (!i.show || i.show(concrete)) && inModule(i.to))
    : [];
  const branchItems = concrete.isBranch ? BRANCH_NAV.filter((i) => inModule(i.to)) : [];

  // 0063: Brick Plant and Pre-cast, where a site this user works with runs them.
  const brick = usePlantAccess('brick');
  const precast = usePlantAccess('precast');
  const plantGroups = [
    { label: 'Brick Plant', show: brick.canUse && inModule('/brick'), items: [
      { to: '/brick/dashboard', label: 'Brick dashboard', icon: '▦' },
      { to: '/brick/production', label: 'Brick production', icon: '▤' },
      { to: '/brick/stock', label: 'Brick stock', icon: '▦' },
      { to: '/brick/dispatch', label: 'Brick dispatch', icon: '⛟' },
      { to: '/brick/targets', label: 'Brick targets', icon: '◎' },
      { to: '/brick/products', label: 'Brick products', icon: '☷' },
    ] },
    { label: 'Pre-cast', show: precast.canUse && inModule('/precast'), items: [
      { to: '/precast/dashboard', label: 'Pre-cast dashboard', icon: '▦' },
      { to: '/precast/production', label: 'Pre-cast production', icon: '▥' },
      { to: '/precast/stock', label: 'Pre-cast stock', icon: '▦' },
      { to: '/precast/dispatch', label: 'Pre-cast dispatch', icon: '⛟' },
      { to: '/precast/targets', label: 'Pre-cast targets', icon: '◎' },
      { to: '/precast/products', label: 'Pre-cast products', icon: '☷' },
    ] },
  ].filter((g) => g.show);

  const link = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.to === '/' || item.to === '/concrete'}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
      aria-label={item.label}
      className={({ isActive }) => `sidebar__link${isActive ? ' is-active' : ''}`}
    >
      <span className="sidebar__icon" aria-hidden>
        {item.icon}
      </span>
      <span className="sidebar__label">{item.label}</span>
    </NavLink>
  );

  return (
    <aside className={`sidebar${open ? ' is-open' : ''}`}>
      <div className="sidebar__brand" title={collapsed ? APP_NAME : undefined}>
        <div className="sidebar__plate sidebar__plate--full">
          <img src={LOGOS.suremix} alt="Suremix" width={1200} height={396} />
        </div>
        <div className="sidebar__plate sidebar__plate--mark">
          <img src={LOGOS.mark} alt="Suremix" width={256} height={256} />
        </div>
        <div className="sidebar__brand-text">
          <div className="sidebar__brand-name">{APP_NAME}</div>
          <div className="sidebar__brand-sub">
            {concrete.isBranch
              ? `${concrete.canUse ? 'Concrete' : 'Branch'} · ${branch?.name ?? 'Branch'}`
              : 'Quarry · Production & Fleet'}
          </div>
        </div>
      </div>

      {myBranches.length > 1 && !collapsed && (
        <div className="sidebar__branch">
          <select aria-label="Branch" className="concrete-branch-select" value={concrete.branchId ?? ''}
            onChange={(e) => selectBranch(e.target.value)}>
            {myBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      )}

      <nav className="sidebar__nav">
        {quarry.map(link)}
        {concreteItems.length > 0 && !concrete.isBranch && (
          <div className="sidebar__group">Concrete</div>
        )}
        {concreteItems.filter((i) => CONCRETE_FIRST.includes(i.to)).map(link)}
        {branchItems.map(link)}
        {concreteItems.filter((i) => !CONCRETE_FIRST.includes(i.to)).map(link)}
        {plantGroups.map((g) => (
          <div key={g.label}>
            <div className="sidebar__group">{g.label}</div>
            {g.items.map(link)}
          </div>
        ))}
        {link(HELP)}
      </nav>

      <button type="button" className="sidebar__collapse" onClick={toggleNav}
        aria-label={collapsed ? 'Expand the menu' : 'Collapse the menu'}
        title={collapsed ? 'Expand the menu' : 'Collapse the menu'}>
        <span className="sidebar__icon" aria-hidden>{collapsed ? '»' : '«'}</span>
        <span className="sidebar__label">Collapse</span>
      </button>

      <div className="sidebar__footer">
        <div className="sidebar__user-initials" title={profile?.full_name ?? ''} aria-hidden>
          {initials(profile?.full_name)}
        </div>
        <div className="small sidebar__user" style={{ marginBottom: 'var(--sp-2)' }}>
          <div>{profile?.full_name ?? '—'}</div>
          <div className="muted" style={{ textTransform: 'capitalize' }}>
            {profile?.role ?? ''}{concrete.isBranch && branch ? ` · ${branch.name}` : ''}
          </div>
        </div>
        {unsent === null ? (
          <button className="btn btn--ghost btn--block" onClick={() => void requestSignOut()}
            title={collapsed ? 'Sign out' : undefined} aria-label="Sign out">
            <span aria-hidden className="sidebar__signout-icon">⏻</span>
            <span className="sidebar__signout-text">Sign out</span>
          </button>
        ) : (
          <div className="col" style={{ gap: 'var(--sp-2)' }} role="alert">
            <div className="small" style={{ color: 'var(--rag-amber)' }}>
              {unsent === 1 ? '1 change on this device has' : `${unsent} changes on this device have`} not
              reached the server yet. Signing out deletes {unsent === 1 ? 'it' : 'them'}.
            </div>
            <button className="btn btn--secondary btn--block" onClick={() => { setUnsent(null); void syncNow(); }}>
              Stay signed in and sync
            </button>
            <button className="btn btn--danger btn--block" onClick={() => { setUnsent(null); void signOut(); }}>
              Sign out anyway
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

function initials(name: string | null | undefined): string {
  if (!name) return '·';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '·';
}
